import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '@/lib/pocketbaseClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // pb.authStore automatically handles localStorage persistence under the hood
  const [isAuthenticated, setIsAuthenticated] = useState(pb.authStore.isValid);
  const [user, setUser] = useState(pb.authStore.model);
  const [currentUser, setCurrentUser] = useState(pb.authStore.model);
  
  const [jobAlertPreferences, setJobAlertPreferences] = useState({
    email: '',
    store: '',
    roleType: ''
  });

  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    // Subscribe to PocketBase auth state changes
    const unsubscribe = pb.authStore.onChange((token, model) => {
      setIsAuthenticated(pb.authStore.isValid);
      setUser(model);
      setCurrentUser(model);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const authData = await pb.collection('users').authWithPassword(email, password, { 
      $autoCancel: false 
    });
    // authData.record contains the full user record including isAdmin
    setCurrentUser(authData.record);
    return authData;
  };

  const signup = async (email, password, passwordConfirm) => {
    await pb.collection('users').create({
      email,
      password,
      passwordConfirm
    }, { 
      $autoCancel: false 
    });
    // Auto-login after successful creation
    return await login(email, password);
  };

  const logout = () => {
    pb.authStore.clear();
  };

  const updateJobAlertPreferences = (preferences) => {
    setJobAlertPreferences(prev => ({ ...prev, ...preferences }));
  };

  const updateContactFormData = (data) => {
    setContactFormData(prev => ({ ...prev, ...data }));
  };

  const resetJobAlertPreferences = () => {
    setJobAlertPreferences({ email: '', store: '', roleType: '' });
  };

  const resetContactFormData = () => {
    setContactFormData({ name: '', email: '', subject: '', message: '' });
  };

  const value = {
    isAuthenticated,
    user,
    currentUser,
    login,
    signup,
    logout,
    jobAlertPreferences,
    updateJobAlertPreferences,
    resetJobAlertPreferences,
    contactFormData,
    updateContactFormData,
    resetContactFormData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};